import type { SavedSearchItem } from '@iconik-io/iconik-api/src/schema_patched/saved_search';
import {
  httpDeleteFavorites,
  httpPostFavorites,
} from '@iconik-io/iconik-api/src/services/assets';
import {
  OBJECT_TYPE_COLLECTIONS,
  type ObjectType,
} from '@iconik-io/iconik-api/src/types/object_types';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { updateEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';

import { CUTTING_ROOM_PROJECT_PANEL } from '../../constants/data';
import {
  ADD_TO_COLLECTION_ADVANCED_MODAL,
  ADD_TO_PORTAL_MODAL,
  AI_METADATA_BULK_MODAL,
  AI_METADATA_MODAL,
  AI_METADATA_REVIEW_MODAL,
  ANALYZE_CONFIRM_MODAL,
  COLLECTION_INFO_MODAL,
  COMMON_ARCHIVE_MODAL,
  COMMON_RESTORE_MODAL,
  COMMON_TRANSFER_MODAL,
  CONFIRM_REMAP_COLLECTION,
  DELETE_ASSETS_MODAL,
  DELETE_COLLECTION_MODAL,
  DELETE_FILES_FROM_STORAGE_MODAL,
  DELETE_KEYFRAMES_MODAL,
  ENTITY_ACLS_MODAL,
  EXPORT_MODAL,
  FACE_RECOGNITION_TRIGGER_CONFIRM_MODAL,
  LINK_ASSET_MODAL,
  METADATA_MODAL,
  NEW_CUTTING_ROOM_PROJECT_MODAL,
  REINDEX_BULK_MODAL,
  REMOVE_FROM_COLLECTION_MODAL,
  RETRANSCODE_CONFIRM_MODAL,
  SHARE_MODAL,
  TRANSCRIBE_CONFIRM_MODAL,
} from '../../constants/modals';
import { ID_COLLECTION } from '../../constants/reducer_ids';
import { openInFcp } from '../../ducks/agent/thunks';
import type { AiMetadataGenerationStatus } from '../../ducks/ai_metadata/types';
import { segmentTrackEvent } from '../../ducks/analytics_tracking/thunks';
import { setCuttingRoomProjectId } from '../../ducks/cutting_room/slice';
import {
  regenerateCollectionKeyframes,
  triggerKeyframeUpload,
} from '../../ducks/entity_keyframes/thunks';
import {
  downloadEditProxyFile,
  downloadSearchItems,
} from '../../ducks/files/thunks';
import { cancellable } from '../../ducks/http/actions';
import { errorMessage } from '../../ducks/messages/thunks';
import { openModal } from '../../ducks/modals_manager/slice';
import type { CategoryObjectType } from '../../ducks/pages/admin_metadata_categories/types';
import { openPanel } from '../../ducks/panels/slice';
import { modifyBranchByID } from '../../ducks/tree/thunks';
import { ICONS } from '../../icons/icon';
import type { AclEntity } from '../../panels/entity_acls/connector';
import type { AppDispatch, GetState } from '../../store/types';
import type { MenuItem } from '../../ui/menus/menu/types';
import { reviewBulkMenu } from './context_menu_utils';

export const options = {
  ENTITY_ACCESS_CONTROL: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Access Control…',
    action: () => (dispatch: AppDispatch) => {
      const entities: AclEntity[] = contextItems.map((item) => ({
        id: item.id,
        aclsType: item.object_type,
      }));

      dispatch(openModal({ id: ENTITY_ACLS_MODAL, contextItems: entities }));
    },
    icon: ICONS.Permission,
    disabled,
    multiple,
  }),

  ENTITY_ANALYZE: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Analyze',
    action: () => (dispatch: AppDispatch) =>
      dispatch(openModal({ id: ANALYZE_CONFIRM_MODAL, contextItems })),
    icon: ICONS.Analyze,
    disabled,
    multiple,
  }),

  ENTITY_REVIEW: (subitems: MenuItem[], multiple?: boolean) => ({
    label: 'Review',
    icon: ICONS.ThumbMixed,
    subitems,
    multiple,
  }),

  ENTITY_APPROVE: (
    contextItems: (SearchItemEntity | SavedSearchItem)[],
    objectType: ObjectType,
    multiple?: boolean,
  ) => reviewBulkMenu(contextItems, objectType, 'Approve', multiple),

  ENTITY_REJECT: (
    contextItems: (SearchItemEntity | SavedSearchItem)[],
    objectType: ObjectType,
    multiple?: boolean,
  ) => reviewBulkMenu(contextItems, objectType, 'Reject', multiple),

  ENTITY_RESET: (
    contextItems: (SearchItemEntity | SavedSearchItem)[],
    objectType: ObjectType,
    multiple?: boolean,
  ) => reviewBulkMenu(contextItems, objectType, 'Reset', multiple),

  ENTITY_ARCHIVE: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Archive…',
    action: () => (dispatch: AppDispatch) =>
      dispatch(openModal({ id: COMMON_ARCHIVE_MODAL, contextItems })),
    icon: ICONS.Archive,
    disabled,
    multiple,
  }),

  ENTITY_DOWNLOAD: (subitems: MenuItem[], multiple?: boolean) => ({
    label: 'Download',
    icon: ICONS.Download,
    subitems,
    multiple,
  }),

  ENTITY_DOWNLOAD_ORIGINAL: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
    amount?: number,
  ) => ({
    label: 'Original',
    action: () => (dispatch: AppDispatch) =>
      dispatch(downloadSearchItems(contextItems, 'original')),
    icon: ICONS.Download,
    multiple,
    disabled,
    amount,
  }),

  ENTITY_DOWNLOAD_EDIT_PROXY: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
    amount?: number,
  ) => ({
    label: 'Edit Proxy',
    action: () => (dispatch: AppDispatch) =>
      dispatch(downloadEditProxyFile(contextItems)),
    icon: ICONS.Download,
    disabled,
    multiple,
    amount,
  }),

  ENTITY_DOWNLOAD_PROXY: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
    amount?: number,
  ) => ({
    label: 'Proxy',
    action: () => (dispatch: AppDispatch) =>
      dispatch(downloadSearchItems(contextItems, 'proxy')),
    icon: ICONS.Download,
    disabled,
    multiple,
    amount,
  }),

  ENTITY_DOWNLOAD_ALL_FORMATS: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
    amount?: number,
  ) => ({
    label: 'All Formats',
    action: () => (dispatch: AppDispatch) =>
      dispatch(downloadSearchItems(contextItems, 'all')),
    icon: ICONS.Download,
    disabled,
    multiple,
    amount,
  }),

  ENTITY_OPEN_ORIGINAL_IN_FCP: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
    amount?: number,
  ) => ({
    label: 'Open in Final Cut Pro',
    action: () => (dispatch: AppDispatch) => dispatch(openInFcp(contextItems)),
    icon: ICONS.Clapper,
    multiple,
    disabled,
    amount,
  }),

  ENTITY_EXPORT: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Export…',
    action: () => (dispatch: AppDispatch) => {
      dispatch(openModal({ id: EXPORT_MODAL, contextItems }));
    },
    icon: ICONS.Export,
    disabled,
    multiple,
  }),

  ENTITY_METADATA_UPDATE: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Update Metadata…',
    action: () => (dispatch: AppDispatch) => {
      dispatch(openModal({ id: METADATA_MODAL, contextItems }));
    },
    icon: ICONS.Metadata,
    disabled,
    multiple,
  }),

  ENTITY_AI_METADATA: (
    contextItems: SearchItemEntity[],
    generationStatus?: AiMetadataGenerationStatus,
    versionId?: string,
  ) => {
    const isPending = generationStatus === 'pending';
    const hasProposal = generationStatus === 'success';
    let label = 'Generate metadata with AI…';
    if (isPending) {
      label = 'Generating AI metadata…';
    } else if (hasProposal) {
      label = 'Review AI-generated metadata…';
    }
    return {
      label,
      action: () => (dispatch: AppDispatch) => {
        dispatch(
          segmentTrackEvent({
            eventName: hasProposal
              ? 'Opened Review AI-Generated Metadata Modal'
              : 'Opened Get AI-Generated Metadata Modal',
            properties: {
              Source: hasProposal ? 'asset page' : 'cogwheel',
            },
          }),
        );
        dispatch(
          openModal({
            id: hasProposal ? AI_METADATA_REVIEW_MODAL : AI_METADATA_MODAL,
            contextItems,
            options: { versionId },
          }),
        );
      },
      icon: ICONS.AiMetadata,
      disabled: isPending,
    };
  },

  AI_METADATA_BULK: (
    contextItems: (SearchItemEntity | SavedSearchItem)[],
    objectType: 'assets' | 'collections' | 'saved_searches',
    source: string,
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Generate metadata with AI…',
    action: () => (dispatch: AppDispatch) => {
      dispatch(
        segmentTrackEvent({
          eventName: 'Opened Bulk Get AI-Generated Metadata Modal',
          properties: {
            Source: source,
          },
        }),
      );
      dispatch(
        openModal({
          id: AI_METADATA_BULK_MODAL,
          contextItems,
          options: { objectType },
        }),
      );
    },
    icon: ICONS.AiMetadata,
    disabled,
    multiple,
  }),

  ENTITY_LINK_UPDATE: (
    contextItems: SearchItemEntity[],
    _multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Update Link…',
    action: () => (dispatch: AppDispatch) => {
      dispatch(openModal({ id: LINK_ASSET_MODAL, contextItems }));
    },
    icon: ICONS.Link,
    disabled,
  }),

  ENTITY_RESTORE: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Restore…',
    action: () => (dispatch: AppDispatch) =>
      dispatch(openModal({ id: COMMON_RESTORE_MODAL, contextItems })),
    icon: ICONS.Restore,
    disabled,
    multiple,
  }),

  ENTITY_REMOVE_FROM_COLLECTION: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Remove from Collection',
    action: () => (dispatch: AppDispatch) =>
      dispatch(
        openModal({
          id: REMOVE_FROM_COLLECTION_MODAL,
          contextItems,
        }),
      ),
    icon: ICONS.CrossMark,
    disabled,
    multiple,
  }),

  ENTITY_RETRANSCODE: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Re-transcode',
    action: () => (dispatch: AppDispatch) =>
      dispatch(openModal({ id: RETRANSCODE_CONFIRM_MODAL, contextItems })),
    icon: ICONS.Transcoder,
    disabled,
    multiple,
  }),

  ENTITY_SHARE: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Share…',
    action: () => (dispatch: AppDispatch) => {
      if (contextItems.some((item) => item.is_blocked)) {
        dispatch(errorMessage({ text: "You can't share blocked assets" }));
        return;
      }
      dispatch(
        openModal({
          id: SHARE_MODAL,
          contextItems,
          options: { sharePath: 'Kebab Menu' },
        }),
      );
    },
    icon: ICONS.Share,
    multiple,
    disabled,
  }),

  ENTITY_TRANSCRIBE: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Transcribe',
    action: () => (dispatch: AppDispatch) =>
      dispatch(openModal({ id: TRANSCRIBE_CONFIRM_MODAL, contextItems })),
    icon: ICONS.Transcription,
    multiple,
    disabled,
  }),

  ENTITY_TRANSFER: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
    useFileSet?: boolean,
  ) => ({
    label: 'Transfer/Copy…',
    action: () => (dispatch: AppDispatch) =>
      dispatch(
        openModal({
          id: COMMON_TRANSFER_MODAL,
          contextItems,
          options: {
            useFileSet,
          },
        }),
      ),
    icon: ICONS.Transfer,
    disabled,
    multiple,
  }),

  ASSET_COVER_IMAGE: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Upload Cover Image',
    action: () => (dispatch: AppDispatch) => {
      dispatch(
        segmentTrackEvent({
          eventName: 'Create Custom Keyframe',
          properties: {
            Using: 'Custom Image',
          },
        }),
      );
      return dispatch(triggerKeyframeUpload(contextItems));
    },
    icon: ICONS.Upload,
    disabled,
    multiple,
  }),

  ASSET_DELETE: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
    filter?: boolean,
  ) => {
    const assets = filter
      ? contextItems.filter((item) => item.object_type === 'assets')
      : contextItems;
    return {
      label: filter ? 'Delete Assets…' : 'Delete…',
      amount: filter ? assets.length : undefined,
      action: () => (dispatch: AppDispatch) =>
        dispatch(openModal({ id: DELETE_ASSETS_MODAL, contextItems: assets })),
      icon: ICONS.TrashBin,
      disabled,
      multiple,
    };
  },

  DELETE_FILES_FROM_STORAGE: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
    filter?: boolean,
  ) => {
    const collections = filter
      ? contextItems.filter((item) => item.object_type === 'collections')
      : contextItems;
    return {
      label: 'Delete files from storage…',
      icon: ICONS.TrashBin,
      amount: filter ? collections.length : undefined,
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: DELETE_FILES_FROM_STORAGE_MODAL,
            contextItems: collections,
          }),
        ),
      disabled,
      multiple,
    };
  },

  COLLECTION_COVER_IMAGE: (subitems: MenuItem[]) => ({
    label: 'Cover Image',
    subitems,
    icon: ICONS.Camera,
  }),

  COLLECTION_COVER_IMAGE_DELETE: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Delete…',
    name: 'DELETE',
    icon: ICONS.TrashBin,
    action: () => (dispatch: AppDispatch) =>
      dispatch(openModal({ id: DELETE_KEYFRAMES_MODAL, contextItems })),
    disabled,
    multiple,
  }),

  COLLECTION_COVER_IMAGE_REGENERATE: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Regenerate',
    action: () => (dispatch: AppDispatch) => {
      const [contextItem] = contextItems;
      dispatch(regenerateCollectionKeyframes(contextItem.id));
    },
    icon: ICONS.Refresh,
    disabled,
    multiple,
  }),

  COLLECTION_COVER_IMAGE_UPLOAD: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    action: () => (dispatch: AppDispatch) => {
      dispatch(
        segmentTrackEvent({
          eventName: 'Create Custom Keyframe',
          properties: {
            Using: 'Custom Image',
          },
        }),
      );
      return dispatch(triggerKeyframeUpload(contextItems));
    },
    icon: ICONS.Upload,
    label: 'Upload',
    disabled,
    multiple,
  }),

  COLLECTION_DELETE: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
    filter?: boolean,
  ) => {
    const collections = filter
      ? contextItems.filter((item) => item.object_type === 'collections')
      : contextItems;
    return {
      label: filter ? 'Delete Collections…' : 'Delete…',
      amount: filter ? collections.length : undefined,
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({ id: DELETE_COLLECTION_MODAL, contextItems: collections }),
        ),
      icon: ICONS.TrashBin,
      disabled,
      multiple,
    };
  },

  COLLECTION_METADATA: (subitems: MenuItem[]) => ({
    label: 'Metadata',
    subitems,
    icon: ICONS.Metadata,
  }),

  COLLECTION_METADATA_UPDATE: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Update Collection Metadata',
    action: () => (dispatch: AppDispatch) => {
      dispatch(openModal({ id: METADATA_MODAL, contextItems }));
    },
    icon: ICONS.Metadata,
    disabled,
    multiple,
  }),

  COLLECTION_METADATA_UPDATE_BULK: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Bulk Metadata update',
    action: () => (dispatch: AppDispatch) => {
      const objectType: CategoryObjectType = 'assets';
      dispatch(
        openModal({
          id: METADATA_MODAL,
          contextItems,
          options: {
            containerObjectType: OBJECT_TYPE_COLLECTIONS,
            objectType,
          },
        }),
      );
    },
    icon: ICONS.Metadata,
    disabled,
    multiple,
  }),

  REMOVE_FROM_FAVORITES: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Remove from favorites',
    action: () => (dispatch: AppDispatch) => {
      let itemsToRemove: { assetIds: string[]; collectionIds: string[] } = {
        assetIds: [],
        collectionIds: [],
      };
      contextItems.forEach((item) => {
        if (item.object_type === 'assets') {
          itemsToRemove = {
            ...itemsToRemove,
            assetIds: [...itemsToRemove.assetIds, item.id],
          };
        }
        if (item.object_type === 'collections') {
          itemsToRemove = {
            ...itemsToRemove,
            collectionIds: [...itemsToRemove.collectionIds, item.id],
          };
        }
      });

      const removeFavorites = (
        objectType: 'assets' | 'collections',
        objectIds: string[],
      ) => {
        dispatch(
          cancellable(
            httpDeleteFavorites(
              {
                object_ids: objectIds,
                object_type: objectType,
              },
              { index_immediately: true },
            ),
          ),
        ).then(() => {
          const entities = Object.fromEntries(
            objectIds.map((id) => [id, { favoured: false }]),
          );

          dispatch(
            updateEntities({
              search_items: entities,
              [objectType]: entities,
            }),
          );
          if (objectType === OBJECT_TYPE_COLLECTIONS) {
            for (const id of objectIds) {
              dispatch(
                modifyBranchByID(ID_COLLECTION, id, { favoured: false }),
              );
            }
          }
          dispatch(
            segmentTrackEvent({
              eventName: 'Removed From Favorites',
              properties: {
                'Entity Type': objectType,
              },
            }),
          );
        });
      };

      if (itemsToRemove.assetIds.length > 0) {
        removeFavorites('assets', itemsToRemove.assetIds);
      }

      if (itemsToRemove.collectionIds.length > 0) {
        removeFavorites('collections', itemsToRemove.collectionIds);
      }
    },
    icon: ICONS.FavouriteFilled,
    disabled,
    multiple,
  }),

  ADD_TO_FAVORITES: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Add to favorites',
    action: () => (dispatch: AppDispatch, getState: GetState) => {
      let itemsToAdd: { assetIds: string[]; collectionIds: string[] } = {
        assetIds: [],
        collectionIds: [],
      };
      contextItems.forEach((item) => {
        if (item.object_type === 'assets') {
          itemsToAdd = {
            ...itemsToAdd,
            assetIds: [...itemsToAdd.assetIds, item.id],
          };
        }
        if (item.object_type === 'collections') {
          itemsToAdd = {
            ...itemsToAdd,
            collectionIds: [...itemsToAdd.collectionIds, item.id],
          };
        }
      });

      const addFavorites = (
        objectType: 'assets' | 'collections',
        objectIds: string[],
      ) =>
        dispatch(
          cancellable(
            httpPostFavorites(
              {
                object_ids: objectIds,
                object_type: objectType,
              },
              { index_immediately: true },
            ),
          ),
        ).then(() => {
          const entities = Object.fromEntries(
            objectIds.map((id) => [id, { favoured: true }]),
          );

          dispatch(
            updateEntities({
              search_items: entities,
              [objectType]: entities,
            }),
          );
          if (objectType === OBJECT_TYPE_COLLECTIONS) {
            for (const id of objectIds) {
              dispatch(modifyBranchByID(ID_COLLECTION, id, { favoured: true }));
            }
          }
          dispatch(
            segmentTrackEvent({
              eventName: `Added To Favorites`,
              properties: {
                Route: getState().routeParams.route,
                'Entity Type': objectType,
              },
            }),
          );
        });

      if (itemsToAdd.assetIds.length > 0) {
        addFavorites('assets', itemsToAdd.assetIds);
      }

      if (itemsToAdd.collectionIds.length > 0) {
        addFavorites('collections', itemsToAdd.collectionIds);
      }
    },
    icon: ICONS.Favourite,
    disabled,
    multiple,
  }),
  COLLECTION_RE_MAP: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Re-map',
    icon: ICONS.Eye,
    action: () => (dispatch: AppDispatch) =>
      dispatch(openModal({ id: CONFIRM_REMAP_COLLECTION, contextItems })),
    disabled,
    multiple,
  }),
  ADD_TO_COLLECTION: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Add to collection…',
    action: () => (dispatch: AppDispatch) => {
      dispatch(
        openModal({ id: ADD_TO_COLLECTION_ADVANCED_MODAL, contextItems }),
      );
    },
    icon: ICONS.Collection,
    disabled,
    multiple,
  }),
  ADD_TO_PORTAL: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Add to portal…',
    action: () => (dispatch: AppDispatch) => {
      dispatch(
        segmentTrackEvent({
          eventName: 'Portal Add Opened',
          properties: { Place: 'asset context menu' },
        }),
      );
      dispatch(openModal({ id: ADD_TO_PORTAL_MODAL, contextItems }));
    },
    icon: ICONS.Playlist,
    disabled,
    multiple,
  }),
  ENTITY_COLLECTION_INFO: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Get Info',
    action: () => (dispatch: AppDispatch) => {
      dispatch(
        segmentTrackEvent({
          eventName: 'Get Collection Info',
        }),
      );
      return dispatch(openModal({ id: COLLECTION_INFO_MODAL, contextItems }));
    },
    icon: ICONS.Info,
    disabled,
    multiple,
  }),
  ENTITY_FACE_RECOGNITION_TRIGGER: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Run face recognition…',
    action: () => (dispatch: AppDispatch) =>
      dispatch(
        openModal({ id: FACE_RECOGNITION_TRIGGER_CONFIRM_MODAL, contextItems }),
      ),
    icon: ICONS.FaceRecognition,
    disabled,
    multiple,
  }),
  COLLECTION_BULK_REINDEX: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Reindex…',
    action: () => (dispatch: AppDispatch) =>
      dispatch(
        openModal({
          id: REINDEX_BULK_MODAL,
          contextItems,
        }),
      ),
    icon: ICONS.Index,
    disabled,
    multiple,
  }),

  CREATE_CUTTING_ROOM_PROJECT: (
    contextItems: SearchItemEntity[],
    multiple?: boolean,
    disabled?: boolean,
  ) => ({
    label: 'Create CuttingRoom Project…',
    action: () => (dispatch: AppDispatch) =>
      dispatch(
        openModal({
          id: NEW_CUTTING_ROOM_PROJECT_MODAL,
          contextItems,
        }),
      ),
    icon: ICONS.Project,
    disabled,
    multiple,
  }),

  OPEN_CUTTING_ROOM_PROJECT: (projectId: string, disabled?: boolean) => ({
    label: 'Open in panel',
    action: () => (dispatch: AppDispatch) => {
      dispatch(setCuttingRoomProjectId(projectId));
      dispatch(openPanel(CUTTING_ROOM_PROJECT_PANEL));
    },
    icon: ICONS.Project,
    disabled,
    multiple: false,
  }),
};
